import { motion } from "motion/react";

/**
 * Loader animado custom — planeta rotacionando com ring e luas em órbita.
 * Usado em estados de processamento mais longos (ex: análise de URL via
 * Firecrawl + Claude que leva 8-25s) onde um spinner genérico não comunica
 * "isso vai demorar um pouco mas tá rolando coisa séria".
 *
 * 100% SVG + motion variants, sem dependências externas. Tamanho default 96.
 *
 * Uso:
 *   <LoadingPlanet />
 *   <LoadingPlanet size={140} label="Analisando página…" />
 */
export function LoadingPlanet({
  size = 96,
  label,
  labelFontSize = 13,
}: {
  size?: number;
  label?: string;
  /** Tamanho da fonte do label. Default 13; telas que precisam de mais
   *  legibilidade (ex: cálculo do diagnóstico ESCALA) passam um valor maior. */
  labelFontSize?: number;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 14,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        aria-label={label ?? "Carregando"}
        role="img"
      >
        <defs>
          <radialGradient id="planetCore" cx="40%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#ffaa55" />
            <stop offset="55%" stopColor="#ff7000" />
            <stop offset="100%" stopColor="#5a1a00" />
          </radialGradient>
          <radialGradient id="planetGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(255,112,0,0.35)" />
            <stop offset="60%" stopColor="rgba(255,112,0,0.10)" />
            <stop offset="100%" stopColor="rgba(255,112,0,0)" />
          </radialGradient>
          <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(255,112,0,0)" />
            <stop offset="50%" stopColor="rgba(255,112,0,1)" />
            <stop offset="100%" stopColor="rgba(255,112,0,0)" />
          </linearGradient>
          <linearGradient id="ringGradBlue" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(122,143,255,0)" />
            <stop offset="50%" stopColor="rgba(122,143,255,0.85)" />
            <stop offset="100%" stopColor="rgba(122,143,255,0)" />
          </linearGradient>
        </defs>

        {/* Glow ambient */}
        <motion.circle
          cx={50}
          cy={50}
          r={42}
          fill="url(#planetGlow)"
          animate={{ scale: [1, 1.08, 1], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "50px 50px" }}
        />

        {/* Estrelas de fundo decorativas */}
        {Array.from({ length: 8 }).map((_, i) => {
          const angle = (i / 8) * Math.PI * 2;
          const r = 44 + (i % 2) * 4;
          const cx = 50 + Math.cos(angle) * r;
          const cy = 50 + Math.sin(angle) * r;
          return (
            <motion.circle
              key={i}
              cx={cx}
              cy={cy}
              r={0.7}
              fill="rgba(255,225,180,0.7)"
              animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{
                duration: 1.5 + (i % 3) * 0.4,
                repeat: Infinity,
                delay: i * 0.18,
                ease: "easeInOut",
              }}
            />
          );
        })}

        {/* Anel externo orbital — rotacionando */}
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "50px 50px" }}
        >
          <ellipse
            cx={50}
            cy={50}
            rx={36}
            ry={11}
            fill="none"
            stroke="url(#ringGrad)"
            strokeWidth={1.4}
            strokeLinecap="round"
            transform="rotate(-18 50 50)"
          />
          {/* Lua orbitando no anel */}
          <circle cx={86} cy={50} r={2.4} fill="#ffaa55" transform="rotate(-18 50 50)" />
        </motion.g>

        {/* Anel interno orbital — sentido contrário, mais rápido */}
        <motion.g
          animate={{ rotate: -360 }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "50px 50px" }}
        >
          <ellipse
            cx={50}
            cy={50}
            rx={28}
            ry={6}
            fill="none"
            stroke="url(#ringGradBlue)"
            strokeWidth={1}
            strokeLinecap="round"
            transform="rotate(20 50 50)"
            opacity={0.7}
          />
          {/* Pequena lua azul */}
          <circle cx={78} cy={50} r={1.6} fill="#7a8fff" transform="rotate(20 50 50)" />
        </motion.g>

        {/* Núcleo do planeta — leve breathing */}
        <motion.circle
          cx={50}
          cy={50}
          r={14}
          fill="url(#planetCore)"
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "50px 50px" }}
        />

        {/* Highlight especular — pequena rotação pra dar vida */}
        <motion.ellipse
          cx={45}
          cy={45}
          rx={4}
          ry={2.5}
          fill="rgba(255,255,255,0.55)"
          animate={{ rotate: [0, 8, -8, 0] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          style={{ transformOrigin: "45px 45px" }}
        />
      </svg>

      {label && (
        <motion.div
          animate={{ opacity: [0.6, 1, 0.6] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          style={{
            fontSize: labelFontSize,
            fontWeight: 500,
            color: "var(--wf-ink-2)",
            fontFamily: "var(--wf-font-body)",
            letterSpacing: "0.005em",
          }}
        >
          {label}
        </motion.div>
      )}
    </div>
  );
}
